import { Phone, Mail, Facebook, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { BUSINESS, SUBURBS } from "../content";
import { track } from "../lib/track";

export const ContactPage = () => (
  <>
    <Seo
      title="Get a Free Cleaning Quote | Contact Pure Bliss Clean"
      description={`Request a free cleaning quote from Pure Bliss Clean. Call ${BUSINESS.phone} or send an enquiry — servicing ${BUSINESS.region}.`}
      path="/contact"
    />
    <PageHero
      eyebrow="Contact"
      titleLines={["Get Your Free", "Cleaning Quote"]}
      intro="Tell us what you need cleaned and where you're located, and we'll be in touch to discuss your quote. Prefer to chat? Give us a call."
    />

    <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Talk To Us</Eyebrow>
            <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">We're Local & Easy To Reach</h2>
          </Reveal>
          <div className="mt-10 space-y-4">
            <Reveal delay={0.05}>
              <a
                href={BUSINESS.phoneHref}
                data-testid="contact-phone-card"
                onClick={() => track("click_to_call", { location: "contact_page" })}
                className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-lg"
              >
                <span className="rounded-2xl bg-teal-50 p-3.5 text-brand-teal"><Phone className="h-5 w-5" /></span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-widest text-slate-500">Call Us</span>
                  <span className="font-display text-lg font-bold text-brand-ink">{BUSINESS.phone}</span>
                </span>
              </a>
            </Reveal>
            {BUSINESS.email && (
              <Reveal delay={0.1}>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  data-testid="contact-email-card"
                  onClick={() => track("email_click", { location: "contact_page" })}
                  className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-lg"
                >
                  <span className="rounded-2xl bg-teal-50 p-3.5 text-brand-teal"><Mail className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500">Email</span>
                    <span className="font-display text-lg font-bold text-brand-ink">{BUSINESS.email}</span>
                  </span>
                </a>
              </Reveal>
            )}
            <Reveal delay={0.15}>
              <a
                href={BUSINESS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-facebook-card"
                onClick={() => track("social_click", { network: "facebook", location: "contact_page" })}
                className="flex items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-teal-300 hover:shadow-lg"
              >
                <span className="rounded-2xl bg-teal-50 p-3.5 text-brand-teal"><Facebook className="h-5 w-5" /></span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-widest text-slate-500">Facebook</span>
                  <span className="font-display text-lg font-bold text-brand-ink">Pure Bliss Clean</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex items-center gap-4">
                  <span className="rounded-2xl bg-teal-50 p-3.5 text-brand-teal"><MapPin className="h-5 w-5" /></span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500">Service Areas</span>
                    <span className="font-display text-lg font-bold text-brand-ink">{BUSINESS.region}</span>
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">
                  Servicing {BUSINESS.baseSuburb} and surrounding suburbs including {SUBURBS.slice(1, 6).join(", ")} and more.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.1} className="lg:col-span-7">
          <QuoteForm source="contact_page" />
        </Reveal>
      </div>
    </section>
  </>
);
